// ImageEditorSettings — the LIVE-WIRING adapter for the Image block (WL-011b + WL-011c).
//
// Adopts the WL-011 ImageEditor into the real craftjs Image node: reads node props via
// useNode(), maps them to the editor value (wiring.js — pure, unit-tested), and routes
// onChange back through setProp while keeping the PrevStyles reducer copy in sync.
//
// WL-011c: this adapter now ALSO owns the image SELECTION pipeline, at full parity with
// the legacy ImageSettings.jsx / UploadMedia / MediaSelection chain:
//   - Upload: the real S3 presigned pipeline (utils/fileUpload.handleFileUpload ->
//     getPresignedUrl POST /api/presigned-url/ -> PUT to S3 -> media moderation ->
//     AVIF->PNG / HEIC->JPEG conversion), progress + error state, then saveRecentMedia.
//   - Browse: the shared MediaBrowser (search + save-external-image).
//   - Recent: the shared RecentMedia (getRecentMedia) picker.
//   - Embed URL: the shared EmbedURL (re-host the pasted image + saveRecentMedia),
//     so an embedded image lands in Recent and the Media Library like an upload
//     (T22.136 -- this tab used to hotlink the raw URL into `src` per keystroke).
//   - Copy URL / Remove.
// The editor stays presentational; this adapter supplies onUpload + renderBrowse/renderRecent.
//
// Wired directly as the Image block's craft.related.settings (Image/index.jsx) —
// the sole Image settings editor.

import React, { useState } from "react";
import { useNode, useEditor } from "@craftjs/core";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { updateImageStyles } from "../../../../redux/reducers/PrevStylesCopy/CodeblockDefaultStyles";
import { toggleSegmentSettings } from "../../../../redux/actions/segmentSettings";
import { getWheelId, getSelectedSegment } from "../../../../redux/selectors/segmentBuilderSelectors";
import { getLoggedInUser, getRandomId } from "../../../../utils/helpers";
import { getWheelUploadPath, handleFileUpload } from "../../../../utils/fileUpload";
import { saveRecentMedia } from "../../../../services/api";
import MediaBrowser from "../../Shared/MediaBrowser";
import RecentMedia from "../../Shared/RecentMedia";
import EmbedURL from "../../Shared/EmbedURL";
import MediaLibraryReuse from "../../../MediaLibrary/MediaLibraryReuse";
import ImageEditor from "../../BlockEditorPane/editors/ImageEditor";
import {
  nodePropsToValue,
  applyValueToProps,
  applyUploadedMediaToProps,
  applyPickedMediaToProps,
} from "./wiring";

const fileExt = (name = "") => {
  const parts = String(name).split(".");
  return parts.length > 1 ? parts.pop() : "";
};

const ImageEditorSettings = ({ segmentName, onBack } = {}) => {
  const {
    id,
    actions: { setProp },
    props,
  } = useNode((node) => ({ props: node.data.props }));
  const {
    actions: { delete: deleteNode },
  } = useEditor();
  const dispatch = useDispatch();

  const wheelId = useSelector(getWheelId);
  // DL-048 adoption: name the open Segment on the reuse modal chip (F2). Prefer an
  // explicit prop; fall back to the selected segment in redux (craft renders this
  // settings pane without a segmentName prop).
  const selectedSegment = useSelector(getSelectedSegment);
  const segName = segmentName || selectedSegment?.settings?.title || null;
  const [uploadStatus, setUploadStatus] = useState("idle");
  const [uploadError, setUploadError] = useState(null);

  const value = nodePropsToValue(props);

  const syncStyleReducer = (draft) => {
    dispatch(
      updateImageStyles({
        width: draft.width,
        alignment: draft.alignment,
        borderRadius: draft.borderRadius,
        borderColor: draft.borderColor,
        borderWidth: draft.borderWidth,
        dropShadowColor: draft.dropShadowColor,
        showImageTitle: draft.showImageTitle,
        showHyperlink: draft.showHyperlink,
        margin: { ...draft.margin },
        isApplyMarginOnAllSides: draft.isApplyMarginOnAllSides,
      })
    );
  };

  const handleChange = (next) => {
    dispatch(toggleSegmentSettings(false));
    setProp((draft) => {
      applyValueToProps(next, draft);
      syncStyleReducer(draft);
    });
  };

  // ---- Upload: the real S3 presigned pipeline (parity with UploadMedia) ----
  const handleUpload = async (file) => {
    setUploadError(null);
    setUploadStatus("uploading");
    const id = getRandomId();
    setProp((draft) => {
      draft.isLoading = true;
    });
    try {
      const user = getLoggedInUser();
      const uploadPath = getWheelUploadPath({
        user_id: user && user.id,
        wheel_id: wheelId,
      });
      const result = await handleFileUpload(file, uploadPath, wheelId, null);
      if (!result || !result.location) {
        throw new Error("Upload did not return a location.");
      }
      const ext = fileExt(file.name);
      setProp((draft) => {
        applyUploadedMediaToProps(
          { id, location: result.location, title: file.name, ext },
          draft
        );
      });
      setUploadStatus("idle");
      try {
        await saveRecentMedia(wheelId, {
          type: "0",
          url: result.location,
          metadata: { title: file.name, type: ext },
        });
      } catch (recentErr) {
        // Non-fatal — recent-media save is best-effort (parity with UploadMedia).
        console.warn("saveRecentMedia failed:", recentErr);
      }
    } catch (err) {
      setProp((draft) => {
        draft.isLoading = false;
      });
      setUploadStatus("error");
      setUploadError(
        typeof err === "string"
          ? err
          : "Image upload failed. Please try again."
      );
      toast.error("Image upload failed.");
    }
  };

  // ---- Browse / Recent picks (parity with MediaSelection handleChange("image")) ----
  const handlePicked = (val = {}) => {
    setProp((draft) => {
      applyPickedMediaToProps(val, draft);
    });
  };

  const handleCopyUrl = async () => {
    const src = value.src;
    if (!src) return;
    try {
      await navigator.clipboard.writeText(src);
      toast.success("URL copied to clipboard");
    } catch (err) {
      console.error("Failed to copy URL:", err);
    }
  };

  // Gordon 12:25 PT spec part 2 (media-delete-block-removal-semantics), the
  // SAME rule ImageGridSettings/CarouselSettings already apply via
  // isLastImageRemoval + deleteNode: "when media is removed from a Wheel
  // Segment and it was the block's sole content -> remove the ENTIRE block
  // (never a skeleton state)". A single Image block's image IS its sole
  // content, unconditionally (there is no multi-item list to fall back to
  // the way ImageGrid/Carousel have) — so Remove must always delete the
  // whole node, not just clear its `image`/`src` props.
  //
  // BEFORE this fix, `handleRemove` only called `setProp(removeImageFromProps)`
  // — it cleared the picture but left the block mounted, rendering
  // ImageBlock's empty-state placeholder forever (a skeleton, not a removed
  // block). That `setProp` DOES mark the wheel dirty and DOES fire a PUT
  // (autosave works fine), which is exactly why this was measured live as
  // "PUT fires (200), chip says saved, but the node count never drops,
  // before or after reload" — the write pipeline was never the broken part;
  // the payload it persisted was.
  const handleRemove = () => {
    deleteNode(id);
  };

  return (
    <ImageEditor
      value={value}
      onChange={handleChange}
      segmentName={segName}
      onBack={onBack}
      onUpload={handleUpload}
      uploadStatus={uploadStatus}
      uploadError={uploadError}
      onCopyUrl={handleCopyUrl}
      onRemove={handleRemove}
      renderBrowse={() => (
        <MediaBrowser mediaType="image" isGifAllowed onConfirm={handlePicked} />
      )}
      renderRecent={() => (
        <RecentMedia type="image" onClickMedia={handlePicked} />
      )}
      renderEmbed={() => (
        <EmbedURL
          type="image"
          media={props.image}
          defaultValue={(props.image && props.image.originalSrc) || ""}
          onConfirm={handlePicked}
          onError={() => handlePicked({ loading: false })}
        />
      )}
      renderLibrary={() => (
        <MediaLibraryReuse
          acceptedTypes={["image"]}
          segmentName={segName}
          contextLabel="an image"
          onInsert={(value) => handlePicked(value)}
        />
      )}
    />
  );
};

export default ImageEditorSettings;
